/**
 * Dev-only helpers for pricing-segment grouping markers in compact itinerary progress rows.
 */

import type { CSSProperties } from "react";
import { type TravelProgressSegmentationPricingSegment } from "../../../types/itinerary/travel-progress-segmentation.types";

export type { TravelProgressSegmentationPricingSegment };

/** Visual marker metadata for one visible leg inside a pricing segment group. */
export interface TravelProgressLegSegmentMarker {
    "segmentColor":     string;
    "isFirstInSegment": boolean;
    "isLastInSegment":  boolean;
}

/** High-contrast palette used to distinguish adjacent pricing segments in debug mode. */
const SEGMENT_MARKER_COLORS = [
    "#16a34a",
    "#e11d48",
    "#0ea5e9",
    "#f59e0b",
    "#7c3aed",
    "#f97316",
] as const;

/**
 * Maps visible leg indexes to segment marker metadata for compact progress rendering.
 *
 * @param pricingSegments Backend pricing segment definitions with leg ranges.
 * @param visibleLegIndexes Leg indexes currently rendered in the compact row.
 * @returns A lookup from leg index to visual marker metadata.
 */
export const buildTravelProgressLegSegmentMarkerMap = (
    pricingSegments: readonly TravelProgressSegmentationPricingSegment[] | undefined,
    visibleLegIndexes: readonly number[],
): Map<number, TravelProgressLegSegmentMarker> => {
    if ( pricingSegments === undefined || pricingSegments.length === 0 || visibleLegIndexes.length === 0 ) {
        return new Map();
    }

    const visibleIndexSet = new Set( visibleLegIndexes );
    const markerMap = new Map<number, TravelProgressLegSegmentMarker>();

    pricingSegments.forEach( ( segment, segmentIndex ) => {
        const firstLegIndex = segment.legRange?.firstLegIndex;
        const lastLegIndex = segment.legRange?.lastLegIndex;

        if (
            firstLegIndex == null ||
            lastLegIndex == null ||
            firstLegIndex < 0 ||
            lastLegIndex < firstLegIndex
        ) {
            return;
        }

        const visibleInSegment = visibleLegIndexes.filter( ( legIndex ) => {
            return legIndex >= firstLegIndex && legIndex <= lastLegIndex;
        } );

        if ( visibleInSegment.length === 0 ) {
            return;
        }

        const segmentColor = SEGMENT_MARKER_COLORS[segmentIndex % SEGMENT_MARKER_COLORS.length];

        visibleInSegment.forEach( ( legIndex, visibleSegmentLegIndex ) => {
            if ( !visibleIndexSet.has( legIndex ) ) {
                return;
            }

            markerMap.set( legIndex, {
                segmentColor,
                "isFirstInSegment": visibleSegmentLegIndex === 0,
                "isLastInSegment":  visibleSegmentLegIndex === visibleInSegment.length - 1,
            } );
        } );
    } );

    return markerMap;
};

/**
 * Resolves the inline style for a leg strip segment marker when debug mode is enabled.
 *
 * @param debugItineraries Whether the debug itineraries overlay is active.
 * @param markerMap Marker lookup built from pricing segments.
 * @param legIndex Target leg index in the itinerary.
 * @returns CSS properties for the marker underline, or an empty object when inactive.
 */
export const getTravelProgressLegSegmentMarkerStyle = (
    debugItineraries: boolean,
    markerMap: ReadonlyMap<number, TravelProgressLegSegmentMarker>,
    legIndex: number,
): CSSProperties => {
    if ( !debugItineraries ) {
        return {};
    }

    const segmentMarker = markerMap.get( legIndex );
    if ( segmentMarker == null ) {
        return {};
    }

    return {
        "boxShadow":               `inset 0 -3px 0 ${ segmentMarker.segmentColor }`,
        "borderBottomLeftRadius":  segmentMarker.isFirstInSegment ? "0.375rem" : undefined,
        "borderBottomRightRadius": segmentMarker.isLastInSegment ? "0.375rem" : undefined,
    };
};
